import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { H3, Text } from 'components/core/Typography';
import { Image } from 'components/core/Image';
import ErrorIcon from 'assets/images/icons/outline.svg';
import ClosedSignIcon from 'assets/images/icons/closed-sign-tilt.svg';
import BookableUnitDetailsV2 from 'components/unit/BookableUnitDetailsV2/BookableUnitDetailsV2';
import './FacilityUnitListV2.scss';
import Filters from 'components/organisms/FacilityPage/Filters';
import useViewportMatch from 'hooks/useViewportMatch';
import useIsDesktopDevice from 'hooks/useIsDesktopDevice';
import { breakpointsByName } from 'config/breakpoints';

// breakpointsByName.facilitySidebar mirrors the raw 841px breakpoint in
// FacilityUnitListV2.scss
const isDesktopWidth = (width) => width >= breakpointsByName.facilitySidebar;

export function FacilityUnitListV2(props) {
	const isDesktopDevice = useIsDesktopDevice();
	const isDesktop = useViewportMatch(isDesktopWidth, isDesktopDevice);
	const {
		storageUnits,
		facility,
		onCreateBooking,
		onExpandUnit,
		expandedUnitId,
		storageType,
		isNewSearchPage,
	} = props;

	const [filteredUnits, setFilteredUnits] = useState(storageUnits);

	useEffect(() => {
		setFilteredUnits(storageUnits);
	}, [storageUnits]);

	const handleFilteredUnitsChange = (units) => {
		setFilteredUnits(units);
	};

	const hasNoStorageUnits = !storageUnits.length;

	return (
		<div
			className={classnames('facility-unit-list-v2-container', {
				'no-storage-units': hasNoStorageUnits,
			})}
		>
			{!hasNoStorageUnits && (
				<aside className="filters-sidebar">
					<Filters
						{...props}
						onFilteredUnitsChange={handleFilteredUnitsChange}
					/>
				</aside>
			)}
			<div className="unit-list-container">
				{hasNoStorageUnits && (
					<div className="no-units-found">
						{isDesktop ? (
							<Image
								src={ErrorIcon}
								alt="No Units Found"
								className="error-icon"
							/>
						) : (
							<Image
								src={ClosedSignIcon}
								alt="No Units Found"
								className="closed-sign-icon"
							/>
						)}
						<div className="no-units-found-text">
							<Text className="no-units-subtitle">
								Sorry! There are no units available for this
								facility on SelfStorage.com at this time.
							</Text>
						</div>
					</div>
				)}
				{!hasNoStorageUnits && !filteredUnits.length && (
					<div className="no-units-found">
						{isDesktop ? (
							<Image
								src={ErrorIcon}
								alt="No Units Found"
								className="error-icon"
							/>
						) : (
							<Image
								src={ClosedSignIcon}
								alt="No Units Found"
								className="closed-sign-icon"
							/>
						)}
						<div className="no-units-found-text">
							<H3
								className="no-units-title"
								color="black"
							>
								No results found
							</H3>
							<Text className="no-units-subtitle">
								Please expand your filter selections to see more
								units
							</Text>
						</div>
					</div>
				)}
				{filteredUnits.length > 0 &&
					filteredUnits.map((unit) => (
						<BookableUnitDetailsV2
							key={unit.id}
							facility={facility}
							unit={unit}
							onCreateBooking={onCreateBooking}
							onExpandUnit={onExpandUnit}
							isExpanded={expandedUnitId === unit.id}
							storageType={storageType}
							isNewSearchPage={isNewSearchPage}
						/>
					))}
			</div>
		</div>
	);
}

FacilityUnitListV2.propTypes = {
	storageUnits: PropTypes.array.isRequired,
	facility: PropTypes.object.isRequired,
	onCreateBooking: PropTypes.func.isRequired,
	onExpandUnit: PropTypes.func.isRequired,
	expandedUnitId: PropTypes.string,
	storageType: PropTypes.string,
	isNewSearchPage: PropTypes.bool,
};
